import { useState } from "react";
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { ArrowLeft, Pencil, QrCode, Loader2, FileText, Upload, Trash2, AlertTriangle, MoveRight, Wrench, Ban, ClipboardCheck, Printer, Eye, ExternalLink } from "lucide-react";
import { DocumentViewerDialog } from "@/components/documents/DocumentViewerDialog";
import { useAssetCalibrations, getCalibrationCertificateUrl } from "@/features/calibration/api";
import { RecordCalibrationDialog } from "@/features/calibration/RecordCalibrationDialog";
import { CalibrationResultBadge } from "@/features/calibration/badges";
import { printCalibrationLabel } from "@/features/calibration/CalibrationLabel";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAsset, useAssetDocuments, useUploadAssetDocument, useDeleteAssetDocument, signedAssetDocUrl, useAuditLogs, useDeleteAsset, useLookups, type AssetListRow } from "@/features/assets/api";
import { useQueryClient } from "@tanstack/react-query";
import { useAssetDisposal } from "@/features/disposals/api";
import { DisposalSummaryCard } from "@/features/disposals/DisposalSummaryCard";
import { isDisposed, DISPOSED_BANNER_COPY } from "@/features/assets/lifecycle";
import { DOC_TYPES } from "@/features/assets/types";
import { StatusBadge, CriticalityBadge, AllocationStatusBadge } from "@/features/assets/badges";
import { QrModal, useQrModalState } from "@/features/assets/QrModal";
import { AllocateAssetDialog } from "@/features/assets/AllocateAssetDialog";
import { AllocationHistory } from "@/features/assets/AllocationHistory";
import { LinkedSparesSection } from "@/features/assets/LinkedSparesSection";
import { AssetLifecycleTimeline } from "@/features/assets/AssetLifecycleTimeline";
import { formatCurrency, formatDate, warrantyState, daysUntil } from "@/features/assets/format";
import { buildAssetQrPayload } from "@/lib/app-config";
import { DynamicFieldValueList } from "@/features/dynamic-fields/DynamicFieldValueList";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useAssetBreakdownSummary, useWorkOrders } from "@/features/work-orders/api";
import { SeverityBadge, WoStatusBadge } from "@/features/work-orders/badges";
import { timeAgo } from "@/features/work-orders/format";
import { useAssetMaintenanceHistory, usePmPlanForAsset } from "@/features/pm/api";
import { toast } from "sonner";
import { assetActions } from "@/features/assets/allocationActions";
import { ActionGuard } from "@/components/auth/ActionGuard";
import { FinancePanel } from "@/features/assets/FinancePanel";
import { InheritedDepreciationCard, ActiveDepreciationProfileCard } from "@/features/finance/InheritedDepreciationCard";
import { PurchasePanel } from "@/features/assets/PurchasePanel";
import { AssetContractsTab } from "@/features/assets/AssetContractsTab";
import { AssetInsuranceTab } from "@/features/assets/AssetInsuranceTab";
import { AssetLicencesTab } from "@/features/compliance/AssetLicencesTab";
import { AssetOnboardingPanel } from "@/features/assets/AssetOnboardingPanel";
import {
  canAllocateAsset, canEditAssetFull, canEditAssetPurchase,
  isAssetOnboardingComplete, onboardingStageLabel,
  canAssignAsset, canReturnAssetAssignment, canCreateTicket,
} from "@/lib/roles";
import type { AppRole } from "@/lib/roles";
import { isBuildingAsset, isVehicleAsset, canTransferAsset, canDisposeBuilding } from "@/features/assets/classification";
import { BuildingDetailsSection } from "@/features/assets/BuildingDetailsSection";
import { VehicleDetailsSection } from "@/features/assets/VehicleDetailsSection";
import { useAssetProcurementLink } from "@/features/assets/generateApi";
import { CurrentAssignmentCard } from "@/features/assets/CurrentAssignmentCard";
import { AssetReliabilityCard } from "@/features/assets/AssetReliabilityCard";
import { AssetCalibrationSummaryCard } from "@/features/assets/AssetCalibrationSummaryCard";
import { AssetHierarchyCard } from "@/features/assets/AssetHierarchyCard";
import { LinkedSubAssetsSection } from "@/features/assets/LinkedSubAssetsSection";
import { ParentAssetCard } from "@/features/assets/ParentAssetCard";
import { AssignAssetCodeDialog } from "@/features/assets/AssignAssetCodeDialog";
import { useSubAssets } from "@/features/assets/subAssetsApi";
import { AssignmentHistoryTab } from "@/features/assets/AssignmentHistoryTab";



export const Route = createFileRoute("/_authenticated/assets/$assetId/")({
  component: AssetProfilePage,
});

const CONDEMN_ROLES = new Set(["super_admin", "biomed_head", "facility_manager", "finance", "management"]);
const DISPOSAL_ROLES = new Set(["super_admin", "biomed_head", "finance"]);
const DELETE_ROLES = new Set(["super_admin"]);
const FINANCE_PANEL_ROLES = new Set(["super_admin", "finance"]);


function AssetProfilePage() {
  const { assetId } = Route.useParams();
  const navigate = useNavigate();
  const { data: asset, isLoading } = useAsset(assetId);
  const { data: existingDisposal } = useAssetDisposal(assetId);
  const { data: me } = useCurrentUser();
  const roles = (me?.roles ?? []) as AppRole[];
  const building = isBuildingAsset(asset);
  const vehicle = isVehicleAsset(asset);
  const canAllocate = canAllocateAsset(roles, asset) && !building;
  const canCondemn = roles.some((r) => CONDEMN_ROLES.has(r)) && isAssetOnboardingComplete(asset) && !building;
  const canDispose = (building ? canDisposeBuilding(roles) : roles.some((r) => DISPOSAL_ROLES.has(r))) && isAssetOnboardingComplete(asset);
  const canEdit = canEditAssetFull(roles, asset);
  const canDelete = roles.some((r) => DELETE_ROLES.has(r));
  const canFinance = roles.some((r) => FINANCE_PANEL_ROLES.has(r));
  const canPurchase = canEditAssetPurchase(roles, asset);
  const canTransfer = canTransferAsset(roles, asset);
  const canAssign = canAssignAsset(roles, asset);
  const canReturn = canReturnAssetAssignment(roles);
  const canCreateNewTicket = canCreateTicket(roles);
  const stageLabel = onboardingStageLabel(asset);
  const onboardingDone = isAssetOnboardingComplete(asset);
  const qrModal = useQrModalState();
  const deleteMut = useDeleteAsset();
  const queryClient = useQueryClient();
  const [allocateOpen, setAllocateOpen] = useState(false);
  const [assignCodeOpen, setAssignCodeOpen] = useState(false);
  const { data: subAssets } = useSubAssets(assetId);
  const subAssetsCount = subAssets?.length ?? 0;

  if (isLoading) return <div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (!asset) return (
    <div className="mx-auto max-w-lg py-20 text-center">
      <h2 className="text-lg font-semibold">You do not have access to this record.</h2>
      <p className="mt-2 text-sm text-muted-foreground">
        This asset belongs to another department or has been removed.
      </p>
    </div>
  );

  const wState = warrantyState(asset.warranty_end_date);
  const wDays = daysUntil(asset.warranty_end_date);
  const disposed = isDisposed(asset);

  return (
    <div className="space-y-4">
      <Button variant="ghost" size="sm" onClick={() => navigate({ to: "/assets" })}><ArrowLeft className="mr-1 h-4 w-4" />Back to Assets</Button>

      <Card>
        <CardContent className="flex flex-wrap items-start justify-between gap-3 p-5">
          <div className="space-y-1">
            {asset.asset_code ? (
              <Badge variant="outline" className="font-mono">{asset.asset_code}</Badge>
            ) : (
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-amber-600 bg-amber-50 border-amber-300 font-medium">
                  No Asset Code
                </Badge>
                {canEdit && !disposed && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 text-xs px-2 gap-1 text-primary border-primary/30 hover:bg-primary/5"
                    onClick={() => setAssignCodeOpen(true)}
                  >
                    Assign Code
                  </Button>
                )}
              </div>
            )}
            <h1 className="text-2xl font-semibold tracking-tight">{asset.category?.name ?? asset.name}</h1>
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge value={asset.status} />
              {!building && <CriticalityBadge value={asset.criticality} />}
              <AllocationStatusBadge value={building ? "Not Applicable" : asset.allocation_status} />
              {stageLabel && !building && <Badge variant="outline" className="border-amber-500/50 text-amber-700 dark:text-amber-300">{stageLabel}</Badge>}
              {(() => {
                const allow = ["Equipment","Infrastructure","Vehicle","IT","Furniture","Other"];
                const t = asset.asset_class && allow.includes(asset.asset_class) ? asset.asset_class : "Unclassified";
                return <Badge variant="outline">{t}</Badge>;
              })()}
              {asset.asset_nature && (
                <Badge variant="outline" className={asset.asset_nature === "Major" ? "border-amber-500 text-amber-600" : ""}>
                  {asset.asset_nature}
                </Badge>
              )}
              {((asset as any).requires_aerb || asset.aerb_licence_no) && (
                <Badge variant="outline" className="border-cyan-500/50 text-cyan-700 bg-cyan-500/10">AERB Regulated</Badge>
              )}
              {((asset as any).requires_pndt || asset.pndt_reg_no) && (
                <Badge variant="outline" className="border-purple-500/50 text-purple-700 bg-purple-500/10">PNDT Regulated</Badge>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {disposed ? (
              <>
                {existingDisposal && (
                  <Button variant="outline" onClick={() => navigate({ to: "/disposals" })}>
                    <FileText className="mr-1 h-4 w-4" />View Disposal Record
                  </Button>
                )}
                <Button variant="outline" onClick={() => qrModal.show(asset)}>
                  <QrCode className="mr-1 h-4 w-4" />QR Label
                </Button>
              </>
            ) : (
              <>
                {(() => {
                  const actions = assetActions(asset);
                  const allocatorBase = roles.some((r) => ["super_admin","biomed_head","facility_manager","purchase_stores"].includes(r));
                  const showAllocateDisabled = allocatorBase && !onboardingDone && (actions.includes("allocate") || actions.includes("allocate_remaining"));
                  return (
                    <>
                      {canAllocate && onboardingDone && actions.includes("allocate") && (
                        <Button onClick={() => setAllocateOpen(true)}>
                          <MoveRight className="mr-1 h-4 w-4" />Allocate
                        </Button>
                      )}
                      {canAllocate && onboardingDone && actions.includes("allocate_remaining") && (
                        <Button onClick={() => setAllocateOpen(true)}>
                          <MoveRight className="mr-1 h-4 w-4" />Allocate Remaining
                        </Button>
                      )}
                      {showAllocateDisabled && (
                        <Button
                          variant="outline"
                          disabled
                          title="Asset onboarding is not complete. Complete technical verification, finance capitalisation, and installation before allocation."
                        >
                          <MoveRight className="mr-1 h-4 w-4" />Allocate
                        </Button>
                      )}
                      {actions.includes("view_allocations") && (
                        <Button variant="outline" onClick={() => navigate({ to: "/assets/$assetId", params: { assetId }, hash: "history" })}>
                          <MoveRight className="mr-1 h-4 w-4" />View Allocations
                        </Button>
                      )}
                      {canTransfer && ((asset.allocated_quantity ?? 0) > 0 || asset.allocation_status === "Allocated" || asset.allocation_status === "Partially Allocated") && (
                        <Button variant="outline" onClick={() => navigate({ to: "/transfers/new", search: { assetId } })}>
                          <MoveRight className="mr-1 h-4 w-4" />Request Transfer
                        </Button>
                      )}
                      {canCondemn && asset.status !== "Condemned" && (
                        <Button variant="outline" onClick={() => navigate({ to: "/condemnation/new", search: { assetId } })}>
                          <Ban className="mr-1 h-4 w-4" />Condemn
                        </Button>
                      )}
                      {existingDisposal ? (
                        <Button variant="outline" onClick={() => navigate({ to: "/disposals" })}>
                          <FileText className="mr-1 h-4 w-4" />View Disposal Record
                        </Button>
                      ) : (
                        canDispose && asset.status === "Condemned" && (
                          <Button variant="outline" onClick={() => navigate({ to: "/disposals/new", search: { assetId } })}>
                            <Trash2 className="mr-1 h-4 w-4" />Record Disposal
                          </Button>
                        )
                      )}
                    </>
                  );
                })()}
                <Button variant="outline" onClick={() => qrModal.show(asset)}><QrCode className="mr-1 h-4 w-4" />QR Label</Button>
                {canCreateNewTicket && (
                  <Button variant="outline" onClick={() => navigate({ to: "/assets/$assetId/report-issue", params: { assetId }, search: { source: "Asset Detail" } as never })}>
                    <AlertTriangle className="mr-1 h-4 w-4" />Create Ticket
                  </Button>
                )}

                <ActionGuard can={canEdit}>
                  <Button onClick={() => navigate({ to: "/assets/$assetId/edit", params: { assetId } })}><Pencil className="mr-1 h-4 w-4" />Edit</Button>
                </ActionGuard>
                <ActionGuard can={canDelete}>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" disabled={deleteMut.isPending}>
                        {deleteMut.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Trash2 className="mr-1 h-4 w-4" />}
                        Delete
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete asset?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This permanently deletes <span className="font-mono">{asset.asset_code}</span> — {asset.name}. Linked documents, allocations, audit logs and history will also be removed. This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          onClick={async () => {
                            try {
                              await deleteMut.mutateAsync(assetId);
                              await queryClient.invalidateQueries({ queryKey: ["assets"] });
                              toast.success("Asset deleted");
                              navigate({ to: "/assets" });
                            } catch (e: unknown) {
                              toast.error((e as Error).message || "Delete failed");
                            }
                          }}
                        >Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </ActionGuard>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Description & Remarks</CardTitle></CardHeader>
        <CardContent className="pt-0 space-y-4">
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Item Description</div>
            <div className="mt-1 whitespace-pre-wrap text-sm">
              {(asset as { item_description?: string | null }).item_description || asset.name || "—"}
            </div>
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Remarks</div>
            <div className="mt-1 whitespace-pre-wrap text-sm">
              {(asset as { remarks?: string | null }).remarks || "—"}
            </div>
          </div>
        </CardContent>
      </Card>

      {disposed && (
        <>
          <Alert className="border-muted-foreground/40 bg-muted/40">
            <Ban className="h-4 w-4" />
            <AlertTitle>Asset Disposed</AlertTitle>
            <AlertDescription>{DISPOSED_BANNER_COPY}</AlertDescription>
          </Alert>
          <DisposalSummaryCard assetId={assetId} />
        </>
      )}

      {!disposed && <AssetOnboardingPanel asset={asset} />}

      {building && <BuildingDetailsSection asset={asset} />}
      {vehicle && <VehicleDetailsSection asset={asset} />}
      {!building && <AllocationSummaryCard asset={asset} />}
      <CurrentAssignmentCard asset={asset} canAssign={canAssign} canReturn={canReturn} />

      {!disposed && !building && <RepeatFailureBanner assetId={assetId} />}

      {!disposed && (asset as { restricted?: boolean }).restricted && (
        <Alert variant="destructive">
          <Ban className="h-4 w-4" />
          <AlertTitle>Asset is RESTRICTED — Do Not Use</AlertTitle>
          <AlertDescription>{(asset as { restriction_note?: string | null }).restriction_note ?? "This asset has been restricted from use."}</AlertDescription>
        </Alert>
      )}

      {!disposed && wState === "expired" && (
        <Alert variant="destructive"><AlertTriangle className="h-4 w-4" /><AlertTitle>Warranty expired</AlertTitle>
          <AlertDescription>Warranty ended on {formatDate(asset.warranty_end_date)} ({Math.abs(wDays ?? 0)} days ago).</AlertDescription>
        </Alert>
      )}
      {!disposed && wState === "expiring" && (
        <Alert className="border-amber-500/40 bg-amber-500/5"><AlertTriangle className="h-4 w-4 text-amber-600" /><AlertTitle>Warranty expiring soon</AlertTitle>
          <AlertDescription>{wDays} days remaining (until {formatDate(asset.warranty_end_date)}).</AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="flex-wrap">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          {!building && <TabsTrigger value="history">Allocation History</TabsTrigger>}
          <TabsTrigger value="documents">Documents</TabsTrigger>
          {!building && <TabsTrigger value="maintenance">Maintenance</TabsTrigger>}
          {!building && !vehicle && <TabsTrigger value="calibration">Calibration</TabsTrigger>}
          <TabsTrigger value="sub-assets" className="gap-1.5">
            Sub Assets
            {subAssetsCount > 0 && (
              <Badge variant="secondary" className="ml-1 text-[11px] px-1.5 py-0 h-4">
                {subAssetsCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="contracts">Contracts</TabsTrigger>
          <TabsTrigger value="insurance">Insurance</TabsTrigger>
          <TabsTrigger value="compliance">Compliance / Licences</TabsTrigger>
          <TabsTrigger value="audit">Audit Trail</TabsTrigger>
          {!building && !vehicle && <TabsTrigger value="spares">Linked Spares</TabsTrigger>}
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab
            asset={asset}
            assetId={assetId}
            canFinance={canFinance && !disposed}
            canPurchase={canPurchase && !disposed}
            canEdit={canEdit && !disposed}
          />
        </TabsContent>
        <TabsContent value="sub-assets">
          <LinkedSubAssetsSection
            assetId={assetId}
            parentAsset={asset}
            canEdit={canEdit && !disposed}
          />
        </TabsContent>
        <TabsContent value="history" className="space-y-4">
          <AssetLifecycleTimeline assetId={assetId} />
          <AllocationHistory assetId={assetId} />
          <AssignmentHistoryTab assetId={assetId} />
        </TabsContent>
        <TabsContent value="documents"><DocumentsTab assetId={assetId} readOnly={disposed} /></TabsContent>
        <TabsContent value="maintenance"><MaintenanceTab assetId={assetId} /></TabsContent>
        <TabsContent value="calibration"><CalibrationTab assetId={assetId} asset={asset} readOnly={disposed} /></TabsContent>
        <TabsContent value="contracts"><AssetContractsTab assetId={assetId} /></TabsContent>
        <TabsContent value="insurance"><AssetInsuranceTab assetId={assetId} /></TabsContent>

        <TabsContent value="compliance"><AssetLicencesTab assetId={assetId} assetClass={asset.asset_class ?? null} asset={asset} /></TabsContent>
        <TabsContent value="audit"><AuditTab assetId={assetId} /></TabsContent>
        <TabsContent value="spares">
          <LinkedSparesSection assetId={assetId} assetBranchId={asset.branch_id ?? null} assetDeptId={asset.custodian_dept_id ?? null} />
        </TabsContent>
      </Tabs>



      <QrModal asset={qrModal.asset} open={qrModal.open} onOpenChange={qrModal.onOpenChange} />
      {!disposed && <AllocateAssetDialog asset={asset} open={allocateOpen} onOpenChange={setAllocateOpen} />}
      <AssignAssetCodeDialog
        assetId={assetId}
        assetName={asset.name}
        open={assignCodeOpen}
        onOpenChange={setAssignCodeOpen}
      />
    </div>
  );
}

function AllocationSummaryCard({ asset }: { asset: AssetListRow }) {
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">Current Allocation Summary</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          <div className="space-y-1"><div className="text-xs text-muted-foreground">Status</div><AllocationStatusBadge value={asset.allocation_status} /></div>
          <Info label="Current Branch" value={asset.branch?.name} />
          <Info label="Current Store" value={asset.store?.name} />
          <Info label="Current Location" value={asset.location?.name} />
          <Info label="Current Department" value={asset.department?.name} />
          <Info label="Custodian User" value={asset.custodian?.full_name || asset.custodian?.email || (asset.custodian_user_id ? "Not assigned" : undefined)} />
          <Info label="Allocation Date" value={formatDate(asset.allocation_date)} />
        </div>
      </CardContent>
    </Card>
  );
}



function Info({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="space-y-1">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-sm font-medium">{value || "—"}</div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4 border-b border-border/50 py-2 text-sm last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{value ?? "—"}</span>
    </div>
  );
}

function ParentAssetLink({ id }: { id: string }) {
  const { data } = useAsset(id);
  const code = data?.asset_code ? data.asset_code : "No Code";
  const label = data ? `${code} - ${data.name}` : "View parent";
  return (
    <Link to="/assets/$assetId" params={{ assetId: id }} className="text-primary hover:underline">
      {label}
    </Link>
  );
}


function OverviewTab({
  asset,
  assetId,
  canFinance,
  canPurchase,
  canEdit,
}: {
  asset: AssetListRow;
  assetId: string;
  canFinance: boolean;
  canPurchase: boolean;
  canEdit: boolean;
}) {
  return (
    <div className="space-y-4">
      {asset.parent_asset_id && (
        <ParentAssetCard
          assetId={assetId}
          parentId={asset.parent_asset_id}
          assetName={asset.name}
        />
      )}
      <div className="grid gap-4 md:grid-cols-2">
        <div id="reliability" className="scroll-mt-24">
          <AssetReliabilityCard assetId={assetId} />
        </div>
        <div id="calibration" className="scroll-mt-24">
          <AssetCalibrationSummaryCard asset={asset} />
        </div>
      </div>
      <div id="hierarchy" className="scroll-mt-24">
        <LinkedSubAssetsSection
          assetId={assetId}
          parentAsset={asset}
          canEdit={canEdit}
          compact={true}
        />
      </div>

    <div className="grid gap-4 md:grid-cols-2">
      <Card>
        <CardHeader><CardTitle className="text-base">Basic</CardTitle></CardHeader>
        <CardContent className="pt-0">
          <InfoRow label="Asset Nature" value={asset.asset_nature} />
          <InfoRow label="Make" value={asset.make} />
          <InfoRow label="Model" value={asset.model} />
          <InfoRow label="Serial No" value={asset.serial_number} />
          <InfoRow label="Year of Manufacture" value={asset.year_of_manufacture} />
          <InfoRow label="Parent Asset" value={asset.parent_asset_id ? <ParentAssetLink id={asset.parent_asset_id} /> : "—"} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Commercial</CardTitle></CardHeader>
        <CardContent className="pt-0">
          <InfoRow label="Supplier" value={asset.supplier?.name || (asset.supplier_id ? "Not assigned" : "—")} />
          <InfoRow label="PO Number" value={asset.po_number} />
          <InfoRow label="Invoice Number" value={asset.invoice_number} />
          <InfoRow label="Invoice Date" value={formatDate(asset.invoice_date)} />
          <InfoRow label="Basic Cost" value={formatCurrency(asset.basic_cost)} />
          <InfoRow label={`GST @ ${asset.gst_rate ?? 0}%`} value={formatCurrency(asset.gst_amount)} />
          <InfoRow label="Freight" value={formatCurrency(asset.freight_cost)} />
          <InfoRow label="Installation" value={formatCurrency(asset.installation_cost)} />
          <InfoRow label="Funding Source" value={asset.funding_source} />
          <InfoRow label="Capitalisation Date" value={formatDate(asset.capitalisation_date)} />
          <InfoRow label="Capitalisation Status" value={asset.capitalisation_date ? "Capitalised" : "Pending"} />
          <InfoRow label="Capitalised Value" value={<span className="text-base font-semibold">{formatCurrency(asset.total_capitalised_value)}</span>} />
          <InfoRow label="Current NBV" value={formatCurrency(asset.nbv ?? asset.total_capitalised_value)} />
        </CardContent>
      </Card>

      <div className="md:col-span-1 space-y-4">
        {asset.capitalisation_status === "Capitalised" ? (
          <ActiveDepreciationProfileCard assetId={asset.id} />
        ) : (
          <InheritedDepreciationCard
            categoryId={asset.category_id}
            book="FINANCIAL"
          />
        )}
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Technical & Compliance</CardTitle></CardHeader>
        <CardContent className="pt-0">
          <InfoRow label="Installation Date" value={formatDate(asset.installation_date)} />
          <InfoRow label="Useful Life (yrs)" value={asset.expected_useful_life} />
          <InfoRow label="Warranty Start" value={formatDate(asset.warranty_start_date)} />
          <InfoRow label="Warranty End" value={formatDate(asset.warranty_end_date)} />
          {((asset as any).requires_aerb || asset.aerb_licence_no || asset.aerb_licence_valid_till) && (<>
            <InfoRow label="AERB Compliance" value={<Badge variant="outline" className="text-xs">AERB Regulated</Badge>} />
            <InfoRow label="AERB Licence" value={asset.aerb_licence_no || "—"} />
            <InfoRow label="AERB Valid Till" value={formatDate(asset.aerb_licence_valid_till)} />
          </>)}
          {((asset as any).requires_pndt || asset.pndt_reg_no || asset.pndt_valid_till) && (<>
            <InfoRow label="PNDT Compliance" value={<Badge variant="outline" className="border-purple-500/50 text-purple-700 bg-purple-500/10 text-xs">PNDT Regulated</Badge>} />
            <InfoRow label="PNDT Reg No" value={asset.pndt_reg_no || "—"} />
            <InfoRow label="PNDT Valid Till" value={formatDate(asset.pndt_valid_till)} />
            <InfoRow label="PNDT Status" value={
              !asset.pndt_valid_till ? (
                <Badge variant="outline" className="text-xs">Registered</Badge>
              ) : new Date(asset.pndt_valid_till) < new Date() ? (
                <Badge variant="destructive" className="text-xs">Expired</Badge>
              ) : (
                <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 text-xs">Active</Badge>
              )
            } />
          </>)}
        </CardContent>
      </Card>

{/*
      <Card>
        <CardHeader><CardTitle className="text-base">Additional Fields</CardTitle></CardHeader>
        <CardContent className="pt-0">
          <DynamicFieldValueList module="assets" recordId={assetId} />
        </CardContent>
      </Card>
*/}
      {canFinance && <div id="finance-panel" className="md:col-span-2 scroll-mt-20"><FinancePanel asset={asset} /></div>}
      {canPurchase && <div id="purchase-panel" className="md:col-span-2 scroll-mt-20"><PurchasePanel asset={asset} /></div>}
      <div className="md:col-span-2"><ProcurementDetailsCard assetId={assetId} /></div>
      </div>
    </div>

  );
}

function ProcurementDetailsCard({ assetId }: { assetId: string }) {
  const { data: link } = useAssetProcurementLink(assetId);
  if (!link || (!link.po && !link.grn && !link.vendor)) return null;
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">Procurement Details</CardTitle></CardHeader>
      <CardContent className="grid gap-3 pt-0 text-sm md:grid-cols-3">
        <div>
          <div className="text-xs text-muted-foreground">Purchase Order</div>
          {link.po ? (
            <Link to="/purchase-orders/$poId" params={{ poId: link.po.id }} className="font-mono text-primary hover:underline">{link.po.po_number}</Link>
          ) : <span>—</span>}
        </div>
        <div><div className="text-xs text-muted-foreground">PO Date</div><div>{link.po?.po_date ?? "—"}</div></div>
        <div>
          <div className="text-xs text-muted-foreground">GRN</div>
          {link.grn ? (
            <Link to="/grns/$grnId" params={{ grnId: link.grn.id }} className="font-mono text-primary hover:underline">{link.grn.grn_no}</Link>
          ) : <span>—</span>}
        </div>
        <div><div className="text-xs text-muted-foreground">GRN Date</div><div>{link.grn?.received_date ?? "—"}</div></div>
        <div><div className="text-xs text-muted-foreground">Vendor</div><div>{link.vendor?.name ?? "—"}</div></div>
        <div><div className="text-xs text-muted-foreground">Invoice</div><div>{link.invoice_number ?? "—"}{link.invoice_date ? ` · ${link.invoice_date}` : ""}</div></div>
        <div><div className="text-xs text-muted-foreground">Purchase Price</div><div className="font-semibold">{link.unit_price != null ? `₹ ${Number(link.unit_price).toFixed(2)}` : "—"}</div></div>
      </CardContent>
    </Card>
  );
}

function DocumentsTab({ assetId, readOnly = false }: { assetId: string; readOnly?: boolean }) {
  const { data: docs } = useAssetDocuments(assetId);
  const upload = useUploadAssetDocument();
  const del = useDeleteAssetDocument();
  const [docType, setDocType] = useState<typeof DOC_TYPES[number]>("Other");
  const [viewer, setViewer] = useState<{ path: string; name: string; size?: number } | null>(null);
  const MAX = 10 * 1024 * 1024;
  const onFiles = async (files: FileList | null) => {
    if (!files) return;
    for (const f of Array.from(files).slice(0, 10)) {
      if (f.size > MAX) { toast.error(`${f.name} exceeds 10MB`); continue; }
      try { await upload.mutateAsync({ assetId, file: f, docType }); }
      catch (e: unknown) { toast.error((e as Error).message); }
    }
    toast.success("Upload complete");
  };
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">Documents</CardTitle>
        {!readOnly && (
          <div className="flex items-center gap-2">
            <Select value={docType} onValueChange={(v) => setDocType(v as typeof DOC_TYPES[number])}>
              <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
              <SelectContent>{DOC_TYPES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
            </Select>
            <label>
              <Button asChild variant="outline" size="sm"><span><Upload className="mr-1 h-4 w-4" />Upload</span></Button>
              <input type="file" multiple hidden accept=".pdf,.jpg,.jpeg,.png,.docx" onChange={(e) => onFiles(e.target.files)} />
            </label>
          </div>
        )}
      </CardHeader>
      <CardContent>
        {docs && docs.length > 0 ? (
          <ul className="divide-y">
            {docs.map((d) => (
              <li key={d.id} className="flex items-center justify-between py-3 text-sm">
                <div className="flex min-w-0 items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <button type="button" className="truncate text-left hover:underline"
                    onClick={() => setViewer({ path: d.file_url, name: d.file_name, size: d.file_size_kb ? d.file_size_kb * 1024 : undefined })}
                  >{d.file_name}</button>
                  <Badge variant="secondary" className="text-xs">{d.doc_type}</Badge>
                  <span className="text-xs text-muted-foreground">{d.file_size_kb ? `${d.file_size_kb} KB` : ""}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" aria-label="View"
                    onClick={() => setViewer({ path: d.file_url, name: d.file_name, size: d.file_size_kb ? d.file_size_kb * 1024 : undefined })}
                  ><Eye className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" aria-label="Open in new tab" onClick={async () => {
                    try { window.open(signedAssetDocUrl(d.file_url), "_blank"); }
                    catch (e: unknown) { toast.error((e as Error).message); }
                  }}><ExternalLink className="h-4 w-4" /></Button>
                  {!readOnly && (
                    <Button variant="ghost" size="icon" aria-label="Delete" onClick={() => del.mutate({ doc: d })}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="py-12 text-center text-sm text-muted-foreground">No documents uploaded yet.</div>
        )}
      </CardContent>
      {viewer && (
        <DocumentViewerDialog
          open={!!viewer} onOpenChange={(o) => !o && setViewer(null)}
          bucket="asset-docs" path={viewer.path} fileName={viewer.name} fileSize={viewer.size}
        />
      )}
    </Card>
  );
}


function EmptyTab({ label, hint }: { label: string; hint: string }) {
  return (
    <Card>
      <CardContent className="py-12 text-center">
        <h3 className="text-lg font-medium">{label}</h3>
        <p className="mt-2 text-sm text-muted-foreground">{hint}</p>
      </CardContent>
    </Card>
  );
}

const AUDIT_FIELD_LABELS: Record<string, string> = {
  asset_code: "Asset Code",
  name: "Asset Name",
  asset_class: "Asset Class",
  category: "Category",
  branch: "Branch",
  location: "Location",
  department: "Department",
  supplier: "Supplier",
  make: "Make",
  model: "Model",
  serial_number: "Serial Number",
  status: "Status",
  criticality: "Criticality",
  allocation_status: "Allocation Status",
  requires_calibration: "Requires Calibration",
  requires_aerb: "Requires AERB",
  requires_pndt: "Requires PNDT",
  custom_fields: "Custom Fields",
  qr_payload: "QR Code Payload",
  purchase_cost: "Purchase Cost",
  purchase_date: "Purchase Date",
  installation_date: "Installation Date",
  warranty_expiry: "Warranty Expiry Date",
};

function getAuditFieldLabel(key: string): string {
  if (key.startsWith("custom_fields.")) {
    const k = key.replace("custom_fields.", "");
    return k.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  }
  return AUDIT_FIELD_LABELS[key] || key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function resolveAuditReference(key: string, val: string, lookups: any): string {
  if (!val || typeof val !== 'string' || !val.includes('-') || val.length !== 36) return val;

  let list: any[] = [];
  let typeName = "Reference";

  if (key.includes('branch')) { list = lookups?.branches || []; typeName = "Branch"; }
  else if (key.includes('department')) { list = lookups?.departments || []; typeName = "Department"; }
  else if (key.includes('location')) { list = lookups?.locations || []; typeName = "Location"; }
  else if (key.includes('supplier') || key.includes('vendor')) { list = lookups?.vendors || []; typeName = "Supplier"; }
  else if (key.includes('category')) { list = lookups?.categories || []; typeName = "Category"; }
  else if (key.includes('store')) { list = lookups?.stores || []; typeName = "Store"; }
  else if (key.includes('user') || key.includes('actor') || key.includes('custodian') || key.includes('updated_by') || key.includes('created_by')) { 
    list = lookups?.users || []; typeName = "User";
  }

  if (list.length > 0) {
    const found = list.find((item: any) => item.id === val);
    if (found) {
      if (found.full_name) return found.full_name;
      if (found.name) return found.name;
      if (found.email) return found.email;
    }
    return `Unknown ${typeName}`;
  }
  return val;
}

function formatAuditValue(key: string, val: unknown, lookups: any): string {
  if (val === null || val === undefined || val === "" || (typeof val === 'object' && Object.keys(val).length === 0)) return "—";
  if (val === true || val === "true" || val === "True") return "Yes";
  if (val === false || val === "false" || val === "False") return "No";
  
  return resolveAuditReference(key, String(val), lookups);
}

function getAuditEventTitle(action: string, changes: any[]): string {
  if (action === "INSERT") return "Asset Created";
  if (action === "DELETE") return "Asset Deleted";
  if (action === "UPDATE") {
    const keys = changes.map(c => c.key);
    if (keys.length === 1 && keys[0] === "status") return "Asset Status Changed";
    if (keys.length === 1 && keys[0] === "allocation_status") return "Allocation Status Changed";
    if (keys.length === 1 && keys[0] === "criticality") return "Criticality Changed";
    if (keys.length === 1 && (keys[0] === "supplier" || keys[0] === "supplier_id")) return "Supplier Changed";
    if (keys.some(k => k.includes("location") || k.includes("department"))) {
      if (keys.every(k => k.includes("location") || k.includes("department") || k.includes("branch"))) {
        return "Asset Location Changed";
      }
    }
    return "Asset Updated";
  }
  return action;
}

function AuditTab({ assetId }: { assetId: string }) {
  const { data: logs, isLoading } = useAuditLogs(assetId);
  const { data: lookups } = useLookups(null);

  if (isLoading) return <div className="py-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>;
  if (!logs || logs.length === 0) return <EmptyTab label="Audit Trail" hint="No changes recorded yet." />;
  
  return (
    <Card>
      <CardContent className="p-0">
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {logs.map((log: any) => {
            const isInsert = log.action === "INSERT";
            let changes: { key: string; before: unknown; after: unknown }[] = [];
            
            if (isInsert) {
               const newFlat = flattenObj(tryParsePythonDict(log.new_data) ?? {});
               const INSERT_WHITELIST = new Set(["asset_code", "name", "asset_class", "category", "category_id", "branch", "branch_id", "department", "department_id", "location", "location_id", "make", "model", "serial_number", "status", "criticality", "supplier", "supplier_id", "vendor", "vendor_id", "allocation_status"]);
               changes = Object.keys(newFlat)
                 .filter(k => INSERT_WHITELIST.has(k) && newFlat[k] !== null && newFlat[k] !== "")
                 .map(k => ({ key: k, before: null, after: newFlat[k] }));
            } else {
               changes = diffRows(log.old_data, log.new_data);
            }

            const eventTitle = getAuditEventTitle(log.action, changes);
            const actorName = log.actor_id ? resolveAuditReference('actor', log.actor_id, lookups) : "System";

            return (
              <li key={log.id} className="p-4 sm:p-5">
                <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-2">
                    {log.action === "UPDATE" && <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 uppercase text-[10px]">Update</Badge>}
                    {log.action === "INSERT" && <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 uppercase text-[10px]">Create</Badge>}
                    {log.action === "DELETE" && <Badge variant="destructive" className="uppercase text-[10px]">Delete</Badge>}
                    <h4 className="font-semibold text-sm text-slate-800 dark:text-slate-200">{eventTitle}</h4>
                  </div>
                  <span className="text-xs text-muted-foreground font-medium flex items-center gap-1.5">
                    {formatDate(log.created_at)}
                    <span className="opacity-50">·</span>
                    <span className="text-slate-600 dark:text-slate-400">{new Date(log.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    <span className="opacity-50">·</span>
                    <span className="text-slate-700 dark:text-slate-300">{actorName}</span>
                  </span>
                </div>

                {changes.length > 0 ? (
                  <div className="space-y-2.5 pl-1">
                    {changes.map((c) => {
                      const label = getAuditFieldLabel(c.key);
                      const before = formatAuditValue(c.key, c.before, lookups);
                      const after = formatAuditValue(c.key, c.after, lookups);
                      
                      if (!isInsert && before === after) return null;
                      
                      return (
                        <div key={c.key} className="grid grid-cols-1 sm:grid-cols-12 gap-1 sm:gap-4 text-xs group">
                          <span className="sm:col-span-4 font-medium text-slate-500 pt-0.5">{label}</span>
                          <div className="sm:col-span-8 flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3">
                            {!isInsert && (
                              <>
                                <span className="text-slate-500 line-through truncate max-w-full sm:max-w-[200px]" title={before}>{before}</span>
                                <MoveRight className="hidden sm:block h-3.5 w-3.5 text-slate-300 flex-shrink-0" />
                              </>
                            )}
                            <span className="text-slate-900 dark:text-slate-100 font-medium break-words">{after}</span>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : <span className="text-xs text-muted-foreground italic">No visible fields changed.</span>}
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}

function tryParsePythonDict(val: unknown): unknown {
  if (typeof val === 'string' && val.trim().startsWith('{') && val.trim().endsWith('}')) {
    try {
      const fixed = val
        .replace(/'/g, '"')
        .replace(/\bTrue\b/g, 'true')
        .replace(/\bFalse\b/g, 'false')
        .replace(/\bNone\b/g, 'null');
      return JSON.parse(fixed);
    } catch {
      return val;
    }
  }
  return val;
}

function flattenObj(obj: any, prefix = ""): Record<string, unknown> {
  let result: Record<string, unknown> = {};
  for (const key in obj) {
    if (obj[key] === null || obj[key] === undefined) continue;
    let val = obj[key];
    val = tryParsePythonDict(val);
    
    if (typeof val === "object" && !Array.isArray(val) && val !== null) {
      result = { ...result, ...flattenObj(val, `${prefix}${key}.`) };
    } else {
      result[`${prefix}${key}`] = val;
    }
  }
  return result;
}

function diffRows(oldD: unknown, newD: unknown): { key: string; before: unknown; after: unknown }[] {
  const oFlat = flattenObj(tryParsePythonDict(oldD) ?? {});
  const nFlat = flattenObj(tryParsePythonDict(newD) ?? {});
  const keys = new Set([...Object.keys(oFlat), ...Object.keys(nFlat)]);
  const result: { key: string; before: unknown; after: unknown }[] = [];
  const skip = new Set(["updated_at", "qr_payload"]);
  for (const k of keys) {
    if (skip.has(k)) continue;
    if (JSON.stringify(oFlat[k]) !== JSON.stringify(nFlat[k])) {
      result.push({ key: k, before: oFlat[k], after: nFlat[k] });
    }
  }
  return result.slice(0, 30);
}


function RepeatFailureBanner({ assetId }: { assetId: string }) {
  const { data } = useAssetBreakdownSummary(assetId);
  if (!data || data.closed_90d < 3) return null;
  return (
    <Alert className="border-amber-500/50 bg-amber-50 text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
      <AlertTriangle className="h-4 w-4" />
      <AlertTitle>Repeat Failure Alert</AlertTitle>
      <AlertDescription>
        This asset has broken down {data.closed_90d}+ times in the past 90 days. Consider condemnation review or vendor escalation.
      </AlertDescription>
    </Alert>
  );
}

function MaintenanceTab({ assetId }: { assetId: string }) {
  const { data: open = [], isLoading: openLoading } = useWorkOrders({});
  const { data: summary } = useAssetBreakdownSummary(assetId);
  const { data: pmPlan } = usePmPlanForAsset(assetId);
  const { data: pmHistory = [] } = useAssetMaintenanceHistory(assetId);
  const rows = open.filter((r) => r.asset_id === assetId);
  const openRows = rows.filter((r) => r.status !== "Verified & Closed");
  const closedRows = rows.filter((r) => r.status === "Verified & Closed" && r.type !== "PM").slice(0, 10);
  const today = new Date().toISOString().slice(0, 10);
  const pmOverdue = pmPlan?.next_due_date && pmPlan.next_due_date < today;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Open Work Orders</div><div className="mt-1 text-2xl font-semibold">{summary?.open_count ?? "—"}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Closed (last 90d)</div><div className="mt-1 text-2xl font-semibold">{summary?.closed_90d ?? "—"}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Total Lifetime</div><div className="mt-1 text-2xl font-semibold">{summary?.total_count ?? "—"}</div></CardContent></Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Preventive Maintenance Plan</CardTitle>
          <Link to="/preventive-maintenance" className="text-xs text-primary hover:underline">Manage plans</Link>
        </CardHeader>
        <CardContent>
          {pmPlan ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 text-sm">
              <div><div className="text-xs text-muted-foreground">Frequency</div><div className="font-medium">Every {pmPlan.frequency_days} days</div></div>
              <div><div className="text-xs text-muted-foreground">Team</div><div className="font-medium">{pmPlan.team}</div></div>
              <div><div className="text-xs text-muted-foreground">Last Done</div><div className="font-medium">{pmPlan.last_done_date ?? "—"}</div></div>
              <div><div className="text-xs text-muted-foreground">Next Due</div><div className={`font-medium ${pmOverdue ? "text-destructive" : ""}`}>{pmPlan.next_due_date ?? "—"}{pmOverdue ? " · Overdue" : ""}</div></div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No active PM plan for this asset.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Wrench className="h-4 w-4" />Open Work Orders</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {openLoading && <div className="py-4 text-center"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></div>}
          {!openLoading && openRows.length === 0 && <p className="text-sm text-muted-foreground">No open work orders.</p>}
          {openRows.map((r) => (
            <Link key={r.id} to="/work-orders/$woId" params={{ woId: r.id }} className="block rounded border p-3 hover:border-primary">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="font-mono text-xs">{r.wo_number}</span>
                  <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${r.type === "PM" ? "bg-blue-500/15 text-blue-700 dark:text-blue-300" : "bg-orange-500/15 text-orange-700 dark:text-orange-300"}`}>{r.type}</span>
                  <SeverityBadge value={r.severity} />
                  <WoStatusBadge value={r.status} />
                  <span className="truncate text-sm text-muted-foreground">{r.problem_description}</span>
                </div>
                <span className="text-xs text-muted-foreground whitespace-nowrap">{timeAgo(r.reported_at)}</span>
              </div>
              <WoContractRowMeta wo={r} />
            </Link>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">PM History</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {pmHistory.length === 0 && <p className="text-sm text-muted-foreground">No PM work orders yet.</p>}
          {(pmHistory as Array<{ id: string; wo_number: string; status: string; due_date: string | null; resolved_at: string | null; closed_at: string | null; action_taken: string | null }>).map((r) => (
            <Link key={r.id} to="/work-orders/$woId" params={{ woId: r.id }} className="flex items-center justify-between rounded border p-3 hover:border-primary">
              <div className="flex items-center gap-3 min-w-0">
                <span className="font-mono text-xs">{r.wo_number}</span>
                <span className="rounded bg-blue-500/15 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 dark:text-blue-300">PM</span>
                <Badge variant="outline" className="text-xs">{r.status}</Badge>
                <span className="truncate text-sm text-muted-foreground">{r.action_taken ?? "Scheduled PM"}</span>
              </div>
              <span className="text-xs text-muted-foreground whitespace-nowrap">Due {r.due_date ?? "—"}</span>
            </Link>
          ))}
        </CardContent>
      </Card>

      {closedRows.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Recently Closed Breakdowns</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {closedRows.map((r) => (
              <Link key={r.id} to="/work-orders/$woId" params={{ woId: r.id }} className="block rounded border p-3 hover:border-primary">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="font-mono text-xs">{r.wo_number}</span>
                    <SeverityBadge value={r.severity} />
                    <span className="truncate text-sm text-muted-foreground">{r.problem_description}</span>
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">{r.closed_at ? timeAgo(r.closed_at) : "—"}</span>
                </div>
                <WoContractRowMeta wo={r} />
              </Link>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function WoContractRowMeta({ wo }: {
  wo: {
    is_under_contract: boolean;
    contract?: { type: string | null; vendor: { name: string } | null } | null;
    reported_at: string;
    acknowledged_at: string | null;
    resolved_at: string | null;
    vendor_call_id: string | null;
    external_cost: number | null;
    spares_cost: number | null;
    contract_id: string | null;
  };
}) {
  const c = wo.contract;
  const respHours = (c as unknown as { response_sla_hours?: number | null } | null)?.response_sla_hours ?? null;
  const resoHours = (c as unknown as { resolution_sla_hours?: number | null } | null)?.resolution_sla_hours ?? null;
  if (!wo.is_under_contract && !wo.contract_id && !wo.external_cost && !wo.spares_cost) return null;

  const slaTag = (label: string, reportedAt: string | null, endAt: string | null, hrs: number | null) => {
    if (hrs == null || !reportedAt) return null;
    const due = new Date(reportedAt).getTime() + hrs * 3_600_000;
    const end = endAt ? new Date(endAt).getTime() : Date.now();
    const met = endAt ? end <= due : end <= due;
    const breached = end > due;
    const state = endAt ? (met ? "Met" : "Breached") : (breached ? "Breached" : "Pending");
    const cls = state === "Met" ? "border-emerald-500/40 text-emerald-700 dark:text-emerald-300"
      : state === "Pending" ? "border-amber-500/40 text-amber-700 dark:text-amber-300"
      : "border-destructive/40 text-destructive";
    return <Badge variant="outline" className={`text-[10px] ${cls}`}>{label}: {state}</Badge>;
  };

  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
      {(wo.is_under_contract || wo.contract_id) && (
        <Badge variant="secondary" className="text-[10px]">Under contract{c?.type ? ` · ${c.type}` : ""}</Badge>
      )}
      {c?.vendor?.name && <span>Vendor: {c.vendor.name}</span>}
      {slaTag("Response", wo.reported_at, wo.acknowledged_at, respHours)}
      {slaTag("Resolution", wo.reported_at, wo.resolved_at, resoHours)}
      {wo.vendor_call_id && <span>Call #{wo.vendor_call_id}</span>}
      {wo.external_cost != null && <span>Ext ₹{wo.external_cost}</span>}
      {wo.spares_cost != null && <span>Spares ₹{wo.spares_cost}</span>}
    </div>
  );
}
function CalibrationTab({ assetId, asset, readOnly = false }: { assetId: string; asset: AssetListRow; readOnly?: boolean }) {
  const assetName = asset.name;
  const assetCode = asset.asset_code;
  const assetBranchId = asset.branch_id ?? null;
  const { data: rows = [], isLoading } = useAssetCalibrations(assetId);
  const [open, setOpen] = useState(false);
  const [openAerb, setOpenAerb] = useState(false);
  async function openCert(p: string) {
    if (p.startsWith("http")) {
      window.open(p, "_blank");
      return;
    }
    const w = window.open("about:blank", "_blank");
    try {
      const u = await getCalibrationCertificateUrl(p);
      if (w) w.location.href = u;
      else window.location.href = u;
    } catch (e) {
      if (w) w.close();
      toast.error((e as Error).message);
    }
  }

  const cfg = asset as unknown as {
    calibration_interval_value?: number | null;
    calibration_interval_unit?: string | null;
    last_calibration_date?: string | null;
    next_calibration_date?: string | null;
    calibration_agency?: string | null;
    calibration_certificate_required?: boolean | null;
    calibration_reminder_days?: number | null;
  };
  const hasAssetSchedule =
    !!cfg.calibration_interval_value && !!cfg.calibration_interval_unit;

  return (
    <div className="space-y-4">
      {hasAssetSchedule && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Calibration Schedule</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 text-sm md:grid-cols-4">
            <div>
              <div className="text-xs text-muted-foreground">Interval</div>
              <div>Every {cfg.calibration_interval_value} {cfg.calibration_interval_unit}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Last Calibration</div>
              <div>{cfg.last_calibration_date ?? "—"}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Next Due</div>
              <div>{cfg.next_calibration_date ?? "—"}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Agency</div>
              <div>{cfg.calibration_agency ?? "—"}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Certificate Required</div>
              <div>{cfg.calibration_certificate_required ? "Yes" : "No"}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Reminder</div>
              <div>{cfg.calibration_reminder_days != null ? `${cfg.calibration_reminder_days} days before` : "—"}</div>
            </div>
            {rows.length === 0 && (
              <div className="md:col-span-4 text-xs text-muted-foreground">
                Calibration schedule is derived from asset configuration. Record a calibration to add history.
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {((asset as any).requires_aerb || asset.aerb_licence_no || asset.aerb_licence_valid_till) && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between">
              <span>AERB & QA Compliance</span>
              <Badge variant="outline" className="border-primary/50 text-primary">AERB Regulated</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 text-sm md:grid-cols-4">
            <div>
              <div className="text-xs text-muted-foreground">AERB Licence Number</div>
              <div className="font-mono text-xs font-semibold">{asset.aerb_licence_no || "—"}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Licence Valid Till</div>
              <div>{asset.aerb_licence_valid_till ? formatDate(asset.aerb_licence_valid_till) : "—"}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">QA Tests Required</div>
              <div>Periodic AERB QA Test</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Licence Status</div>
              <div>
                {!asset.aerb_licence_valid_till ? (
                  <Badge variant="outline">Registered</Badge>
                ) : new Date(asset.aerb_licence_valid_till) < new Date() ? (
                  <Badge variant="destructive">Expired</Badge>
                ) : (
                  <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20">Active</Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {((asset as any).requires_pndt || asset.pndt_reg_no || asset.pndt_valid_till) && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between">
              <span>PNDT Compliance</span>
              <Badge variant="outline" className="border-purple-500/50 text-purple-700 bg-purple-500/10">PNDT Regulated</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 text-sm md:grid-cols-4">
            <div>
              <div className="text-xs text-muted-foreground">PNDT Registration Number</div>
              <div className="font-mono text-xs font-semibold">{asset.pndt_reg_no || "—"}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Registration Valid Till</div>
              <div>{asset.pndt_valid_till ? formatDate(asset.pndt_valid_till) : "—"}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Regulatory Authority</div>
              <div>Appropriate Authority (PNDT)</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Registration Status</div>
              <div>
                {!asset.pndt_valid_till ? (
                  <Badge variant="outline">Registered</Badge>
                ) : new Date(asset.pndt_valid_till) < new Date() ? (
                  <Badge variant="destructive">Expired</Badge>
                ) : (
                  <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20">Active</Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Calibration & QA history</CardTitle>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => {
              const last = rows[0];
              printCalibrationLabel({
                asset_code: assetCode, asset_name: assetName,
                done_date: last?.calibration_date ?? null, next_due_date: last?.next_due_date ?? null,
                certificate_no: last?.certificate_number ?? null, agency: last?.calibration_type === "Vendor" ? last?.vendor_name || "Vendor" : "Internal",
                qrPayload: buildAssetQrPayload(assetCode),
              });
            }} disabled={rows.length === 0}><Printer className="mr-1 h-3 w-3" />Print Label</Button>
            {!readOnly && (asset as any).requires_calibration && (
              <Button size="sm" onClick={() => setOpen(true)}><ClipboardCheck className="mr-1 h-3 w-3" />Record Calibration</Button>
            )}
            {!readOnly && (asset as any).requires_aerb && (
              <Button size="sm" variant="outline" onClick={() => setOpenAerb(true)}><ClipboardCheck className="mr-1 h-3 w-3" />Record AERB QA</Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? <div className="grid place-items-center p-10"><Loader2 className="h-5 w-5 animate-spin" /></div> :
          rows.length === 0 ? <div className="p-6 text-center text-sm text-muted-foreground">No calibration or QA records yet.</div> : (
            <Table>
              <TableHeader><TableRow><TableHead>Kind</TableHead><TableHead>Done</TableHead><TableHead>Next Due</TableHead><TableHead>Agency</TableHead><TableHead>Cert</TableHead><TableHead>Result</TableHead><TableHead className="text-right">Doc</TableHead></TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="text-sm">{r.kind}</TableCell>
                    <TableCell className="text-sm">{r.calibration_date ?? "—"}</TableCell>
                    <TableCell className="text-sm">{r.next_due_date ?? "—"}</TableCell>
                    <TableCell className="text-sm">{r.calibration_type === "Vendor" ? r.vendor_name || "Vendor" : "Internal"}</TableCell>
                    <TableCell className="font-mono text-xs">{r.certificate_number ?? "—"}</TableCell>
                    <TableCell><CalibrationResultBadge value={r.status as any} /></TableCell>
                    <TableCell className="text-right">{r.certificate_file ? <Button size="sm" variant="ghost" onClick={() => openCert(r.certificate_file!)}><FileText className="h-3 w-3" /></Button> : "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
        <RecordCalibrationDialog open={open} onOpenChange={setOpen} assetId={assetId} assetName={assetName} assetCode={assetCode} assetBranchId={assetBranchId} />
        <RecordCalibrationDialog open={openAerb} onOpenChange={setOpenAerb} assetId={assetId} assetName={assetName} assetCode={assetCode} assetBranchId={assetBranchId} kind="AERB QA" />
      </Card>
    </div>
  );
}

